import {useMemo, useState} from 'react'
import {useQuery} from '@tanstack/react-query'
import {useNavigate} from 'react-router-dom'
import {TypePickerDialog} from '@/components/shared/TypePickerDialog'
import {Plus} from 'lucide-react'
import {Button} from '@/components/ui/button'
import {Icon} from '@/components/shared/Icon'
import {permissions} from '@/lib/permissions'
import {usePageTitle} from '@/hooks/use-page-title'
import {useServerPagination, SortDir} from '@/hooks/use-server-pagination'
import {useEmbeddedListProps} from '@/hooks/use-embedded-list-props'
import {ResourceControllerResourcesEndpoint} from '@/types/endpoint-contracts'
import {omitContractFilters} from '@/types/endpoint-contract'
import {useI18n} from '@/i18n'
import {useServices} from '@/services/context'
import {routes} from '@/lib/routes'
import {TableEmptyState} from '@/components/shared/TableEmptyState'
import {UnifiedSearchBar} from '@/components/shared/UnifiedSearchBar'
import {Badge, BadgeVariant} from '@/components/ui/badge'
import {
    Table,
    TableBody,
    TableCell,
    TableHead,
    TableHeader,
    TableRow,
} from '@/components/ui/table'
import {Pagination} from '@/components/ui/pagination'
import {useAppConfig, labelOf} from '@/hooks/use-app-config'
import {PageLayout} from '@/components/shared/PageLayout'
import {ClickableTableRow} from '@/components/shared/ClickableTableRow'
import {TextLink} from '@/components/shared/TextLink'
import {MutedText} from '@/components/shared/MutedText'
import {CONCEPT_ICON, type ConceptKey} from '@/lib/concepts'

export interface PromotedResourceListTheme {
    title: string
    icon?: ConceptKey
    countLabel?: (params: {count: number}) => string
    createLabel?: string
}

export interface ResourcesProps {
    /** Pinned resourceType — hides the type filter and re-themes the page chrome (issue 301). */
    lockedType?: string
    /** Optional re-themed page chrome for the promoted by-type route. */
    themedAs?: PromotedResourceListTheme
    embedded?: boolean
    ownedByMe?: boolean
    emptyStateTitle?: string
}

const RESOURCE_TYPE_PARAM = 'resourceType'

export function Resources({lockedType, themedAs, embedded, ownedByMe, emptyStateTitle}: ResourcesProps = {}) {
    const services = useServices()
    const i18n = useI18n()
    const navigate = useNavigate()
    usePageTitle(i18n.resources.pageTitle)

    const effectiveContract = useMemo(
        () => lockedType
            ? omitContractFilters(ResourceControllerResourcesEndpoint, [RESOURCE_TYPE_PARAM])
            : ResourceControllerResourcesEndpoint,
        [lockedType],
    )

    const {
        page, pageSize, queryString,
        setPage, setPageSize, pageSizeOptions,
        searchAndFilters, setSearchAndFilters,
    } = useServerPagination({
        endpoint: ResourceControllerResourcesEndpoint,
        sortable: true,
        defaultSortBy: ResourceControllerResourcesEndpoint.sort.DISPLAY_NAME,
        defaultSortDir: SortDir.ASC,
    })

    const {resourceTypes, dataClassificationTypes: classifications} = useAppConfig()
    const [typePickerOpen, setTypePickerOpen] = useState(false)

    const headerTitle = themedAs?.title ?? i18n.resources.allResources

    const {listParams, canCreate, title, emptyMessage} = useEmbeddedListProps({
        contract: ResourceControllerResourcesEndpoint,
        ownedByMe,
        baseQueryString: queryString,
        emptyStateTitle,
        defaultTitle: headerTitle,
        createPermission: permissions.resources.create,
    })

    const requestParams = useMemo(() => {
        const params = {...listParams}
        if (lockedType) params[RESOURCE_TYPE_PARAM] = lockedType
        return params
    }, [listParams, lockedType])

    const {data: resourcesData} = useQuery({
        queryKey: ['resource-list', queryString, page, pageSize, lockedType, ownedByMe],
        queryFn: () => services.resources.list(requestParams),
    })

    const resources = resourcesData?.content || []
    const totalItems = resourcesData?.totalElements || 0
    const totalPages = resourcesData?.totalPages || 0

    const hasActiveFilters = useMemo(() => {
        return !!searchAndFilters.q
            || Object.keys(searchAndFilters.filters).length > 0
            || Object.keys(searchAndFilters.dynamicAttributes).length > 0
    }, [searchAndFilters])

    const headerIcon = themedAs?.icon ? CONCEPT_ICON[themedAs.icon] : CONCEPT_ICON.resource
    const countLabel = themedAs?.countLabel
        ? themedAs.countLabel({count: totalItems})
        : i18n.resources.resourceCount({count: totalItems})

    const getClassificationVariant = (classification: string | null): BadgeVariant => {
        if (!classification) return BadgeVariant.OUTLINE
        if (classification.toLowerCase() === 'restricted') return BadgeVariant.DESTRUCTIVE
        return BadgeVariant.OUTLINE
    }

    return (
        <PageLayout
            variant="list"
            icon={headerIcon}
            title={title}
            count={totalItems}
            description={`${countLabel}${hasActiveFilters ? ` ${i18n.common.matchingFilters}` : ''}`}
            data-testid="resources-list"
            embedded={embedded}
            actions={canCreate ? (
                <Button
                    size="sm"
                    onClick={() => setTypePickerOpen(true)}
                    data-testid="btn-new-resource"
                >
                    <Icon icon={Plus} className="mr-1"/>
                    {themedAs?.createLabel ?? i18n.resources.createResource}
                </Button>
            ) : undefined}
            filters={
                <UnifiedSearchBar
                    contract={effectiveContract}
                    attributeEntityType="resource"
                    value={searchAndFilters}
                    onChange={setSearchAndFilters}
                    placeholder={i18n.resources.searchByName}
                    lockedDiscriminatorValues={lockedType ? [lockedType] : undefined}
                />
            }
            pagination={totalItems > 0 ? (
                <Pagination
                    page={page}
                    pageSize={pageSize}
                    totalItems={totalItems}
                    totalPages={totalPages}
                    onPageChange={setPage}
                    onPageSizeChange={setPageSize}
                    pageSizeOptions={pageSizeOptions}
                />
            ) : undefined}
        >
            <Table>
                <TableHeader>
                    <TableRow>
                        <TableHead data-testid="col-name">{i18n.common.name}</TableHead>
                        <TableHead data-testid="col-system">{i18n.resources.system}</TableHead>
                        <TableHead data-testid="col-type">{i18n.common.type}</TableHead>
                        <TableHead data-testid="col-classification">{i18n.systems.classification}</TableHead>
                    </TableRow>
                </TableHeader>
                <TableBody>
                    {resources.map((resource) => (
                        <ClickableTableRow key={resource.id} to={routes.systems.resources.detail(resource.id)}>
                            <TableCell className="font-medium">
                                <TextLink
                                    to={routes.systems.resources.detail(resource.id)}
                                    testId={`link-resource-detail-${resource.id}`}
                                    stopPropagation
                                >
                                    {resource.derivedDisplayName}
                                </TextLink>
                                <MutedText size="sm" className="block">
                                    {resource.nativeKey}
                                </MutedText>
                            </TableCell>
                            <TableCell>
                                <TextLink
                                    to={routes.systems.detail(resource.targetSystem.id)}
                                    testId={`link-system-detail-${resource.targetSystem.id}`}
                                    stopPropagation
                                >
                                    {resource.targetSystem.displayName}
                                </TextLink>
                            </TableCell>
                            <TableCell>
                                <Badge variant="outline">
                                    {labelOf(resourceTypes, resource.resourceType)}
                                </Badge>
                            </TableCell>
                            <TableCell>
                                {resource.dataClassification && (
                                    <Badge variant={getClassificationVariant(resource.dataClassification)}>
                                        {labelOf(classifications, resource.dataClassification)}
                                    </Badge>
                                )}
                            </TableCell>
                        </ClickableTableRow>
                    ))}
                    {resources.length === 0 && (
                        <TableEmptyState
                            colSpan={4}
                            hasFilters={hasActiveFilters}
                            matchMessage={i18n.resources.noResourcesMatch}
                            emptyMessage={emptyMessage ?? i18n.resources.noResourcesFound}
                        />
                    )}
                </TableBody>
            </Table>
            <TypePickerDialog
                open={typePickerOpen}
                onOpenChange={setTypePickerOpen}
                title={i18n.resources.pickType}
                description={i18n.resources.pickTypePrompt}
                types={resourceTypes}
                testIdPrefix="pick-resource-type"
                onPick={(code) => {
                    setTypePickerOpen(false)
                    navigate(routes.systems.resources.createByType(code))
                }}
            />
        </PageLayout>
    )
}
